import { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { Icon } from '../components/Icons'
import { BASE_URL } from '../data/baseurl'
import AddPackage from './AddPackage'
import logo from '../assets/logo/Dira-Holidays-Logo_navigation.png'

const API_URL = `${BASE_URL}/packages`

export default function ManagePackages() {
  const navigate = useNavigate()
  const [packages, setPackages] = useState([])
  const [loading, setLoading] = useState(true)
  const [deleting, setDeleting] = useState(null)
  const [error, setError] = useState('')
  const [toast, setToast] = useState('')
  const [view, setView] = useState('list') // 'list', 'add', 'edit'
  const [editingPkg, setEditingPkg] = useState(null)
  const [confirmDeletePkg, setConfirmDeletePkg] = useState(null)

  const showToast = (msg) => {
    setToast(msg)
    setTimeout(() => setToast(''), 3500)
  }

  // Prevent background scroll and support ESC key when delete modal is open
  useEffect(() => {
    if (confirmDeletePkg) {
      document.body.style.overflow = 'hidden'
      const handleKeyDown = (e) => {
        if (e.key === 'Escape') setConfirmDeletePkg(null)
      }
      window.addEventListener('keydown', handleKeyDown)
      return () => {
        document.body.style.overflow = 'unset'
        window.removeEventListener('keydown', handleKeyDown)
      }
    } else {
      document.body.style.overflow = 'unset'
    }
  }, [confirmDeletePkg])

  const fetchPackages = useCallback(async () => {
    try {
      setLoading(true)
      setError('')
      const res = await fetch(API_URL)
      if (!res.ok) throw new Error('Failed to fetch packages')
      const data = await res.json()
      setPackages(data)
    } catch (err) {
      setError('Could not connect to backend. Make sure the server is running on port 5000.')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchPackages()
  }, [fetchPackages])

  const promptDelete = (pkg) => {
    setConfirmDeletePkg(pkg)
  }

  const handleDelete = async () => {
    if (!confirmDeletePkg) return
    const pkg = confirmDeletePkg
    setConfirmDeletePkg(null)

    setDeleting(pkg._id)
    try {
      const res = await fetch(`${API_URL}/${pkg._id}`, { method: 'DELETE' })
      if (!res.ok) {
        const err = await res.json()
        throw new Error(err.message || 'Delete failed')
      }
      setPackages((prev) => prev.filter((p) => p._id !== pkg._id))
      showToast(`✅ "${pkg.name}" deleted successfully.`)
    } catch (err) {
      showToast(`❌ Error: ${err.message}`)
    } finally {
      setDeleting(null)
    }
  }

  return (
    <div
      className="page-enter admin-page-container"
      style={{ background: '#f8fafc', minHeight: '100vh', padding: '30px 20px 60px', display: 'flex', flexDirection: 'column' }}
    >
      {/* Top Logo Header */}
      <div style={{ maxWidth: '1100px', width: '100%', margin: '0 auto 24px', display: 'flex', justifyContent: 'flex-start' }}>
        <button
          onClick={() => navigate('/')}
          style={{
            display: 'flex',
            alignItems: 'center',
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
          }}
        >
          <img
            src={logo}
            alt="Dira Holidays Logo"
            style={{
              width: 'auto',
              height: '50px',
              borderRadius: '10px',
              flexShrink: 0,
            }}
          />
        </button>
      </div>
      {/* Delete Confirmation Modal (Rendered in Body Portal for True Center Viewport Alignment) */}
      {confirmDeletePkg && createPortal(
        <div
          onClick={(e) => {
            if (e.target === e.currentTarget) setConfirmDeletePkg(null)
          }}
          style={{
            position: 'fixed',
            inset: 0,
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 999999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(13, 17, 23, 0.75)',
            backdropFilter: 'blur(5px)',
            WebkitBackdropFilter: 'blur(5px)',
            padding: '20px',
            boxSizing: 'border-box',
          }}
        >
          <div
            className="modal-enter"
            style={{
              background: '#fff',
              borderRadius: '16px',
              width: '100%',
              maxWidth: '400px',
              padding: '32px',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.35)',
              textAlign: 'center',
              position: 'relative',
            }}
          >
            <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
              <Icon name="trash" size={26} color="#ef4444" />
            </div>
            <h3 style={{ fontSize: '20px', fontWeight: 800, color: '#0d1117', marginBottom: '10px' }}>Delete Package?</h3>
            <p style={{ fontSize: '13px', color: '#6b7a8f', lineHeight: 1.55, marginBottom: '28px' }}>
              Are you sure you want to permanently delete <strong>{confirmDeletePkg.name}</strong>?<br/>This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={() => setConfirmDeletePkg(null)}
                style={{ flex: 1, padding: '12px 0', borderRadius: '10px', background: '#f8fafc', border: '1.5px solid #e2e8f0', fontSize: '13px', fontWeight: 700, color: '#3d4a5c', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                style={{ flex: 1, padding: '12px 0', borderRadius: '10px', background: '#ef4444', border: '1.5px solid #ef4444', fontSize: '13px', fontWeight: 700, color: '#fff', cursor: 'pointer', boxShadow: '0 4px 12px rgba(239, 68, 68, 0.2)' }}
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Toast (Rendered in Portal) */}
      {toast && createPortal(
        <div
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 999999,
            background: toast.startsWith('✅') ? '#ecfdf5' : '#fef2f2',
            border: `1px solid ${toast.startsWith('✅') ? '#86efac' : '#fca5a5'}`,
            color: toast.startsWith('✅') ? '#15803d' : '#b91c1c',
            padding: '14px 20px',
            borderRadius: '10px',
            fontSize: '13px',
            fontWeight: 700,
            boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
            maxWidth: '340px',
          }}
        >
          {toast}
        </div>
      )}

      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
        {/* Header */}
        <div
          className="admin-header-card"
          style={{
            background: 'linear-gradient(135deg, #0d1117 0%, #0202bf 100%)',
            borderRadius: '16px',
            padding: '36px 36px 30px',
            color: '#fff',
            marginBottom: '28px',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'radial-gradient(circle at 85% 20%,rgba(255,182,0,0.08),transparent 50%)',
              pointerEvents: 'none',
            }}
          />
          <div
            className="admin-header-inner"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              flexWrap: 'wrap',
              gap: '16px',
            }}
          >
            <div>
              <span
                style={{
                  fontSize: '10px',
                  fontWeight: 800,
                  letterSpacing: '2.5px',
                  color: 'rgba(255,182,0,0.8)',
                  textTransform: 'uppercase',
                }}
              >
                Admin Panel
              </span>
              <h1 style={{ fontSize: '28px', fontWeight: 900, margin: '6px 0 6px', color: '#fff' }}>
                Manage Packages
              </h1>
              <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)', margin: 0 }}>
                {loading
                  ? 'Loading...'
                  : `${packages.length} package${packages.length !== 1 ? 's' : ''} in database`}
              </p>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={fetchPackages}
                style={{
                  padding: '10px 18px',
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '12px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <Icon name="refresh" size={14} color="#fff" /> Refresh
              </button>
              <button
                onClick={() => setView(view === 'add' ? 'list' : 'add')}
                style={{
                  padding: '10px 20px',
                  background: '#ffb600',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#0d1117',
                  fontSize: '12px',
                  fontWeight: 800,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: '0 4px 12px rgba(255,182,0,0.3)',
                }}
              >
                {view === 'add' ? (
                  'Back to List'
                ) : (
                  <>
                    <Icon name="plus" size={14} color="#0d1117" /> Add Package
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {view === 'add' || view === 'edit' ? (
          <div style={{ marginTop: '20px' }}>
            <AddPackage
              embedded
              editPackage={editingPkg}
              onSuccess={() => {
                setView('list')
                setEditingPkg(null)
                fetchPackages()
                showToast(`✅ Package ${view === 'add' ? 'added' : 'updated'} successfully!`)
              }}
              onCancel={() => {
                setView('list')
                setEditingPkg(null)
              }}
            />
          </div>
        ) : (
          <>
            {/* Error */}
            {error && (
              <div
                style={{
                  background: '#fef2f2',
                  border: '1px solid #fca5a5',
                  color: '#b91c1c',
                  padding: '14px 18px',
                  borderRadius: '10px',
                  marginBottom: '20px',
                  fontSize: '13px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                ⚠️ {error}
                <button
                  onClick={fetchPackages}
                  style={{
                    marginLeft: 'auto',
                    background: '#fee2e2',
                    border: 'none',
                    color: '#b91c1c',
                    padding: '4px 12px',
                    borderRadius: '6px',
                    fontSize: '11px',
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  Retry
                </button>
              </div>
            )}

            {/* Loading */}
            {loading && (
              <div style={{ textAlign: 'center', padding: '80px 0', color: '#6b7a8f' }}>
                <div
                  style={{
                    fontSize: '32px',
                    marginBottom: '12px',
                    animation: 'spin 1s linear infinite',
                    display: 'inline-block',
                  }}
                >
                  ⏳
                </div>
                <div style={{ fontSize: '14px', fontWeight: 600 }}>
                  Loading packages from backend...
                </div>
              </div>
            )}

            {/* Package List */}
            {!loading && packages.length === 0 && !error && (
              <div
                style={{
                  textAlign: 'center',
                  padding: '80px 0',
                  color: '#6b7a8f',
                  background: '#fff',
                  borderRadius: '12px',
                  border: '1.5px solid #e2e8f0',
                }}
              >
                <div style={{ fontSize: '40px', marginBottom: '12px' }}>📦</div>
                <div style={{ fontSize: '16px', fontWeight: 700, marginBottom: '6px' }}>
                  No packages found
                </div>
                <div style={{ fontSize: '13px', marginBottom: '24px' }}>
                  Add your first package to get started.
                </div>
                <button
                  onClick={() => setView('add')}
                  style={{
                    padding: '12px 28px',
                    background: '#0202bf',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '13px',
                    fontWeight: 800,
                    cursor: 'pointer',
                  }}
                >
                  + Add First Package
                </button>
              </div>
            )}

            {!loading && packages.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {packages.map((pkg, idx) => (
                  <div
                    key={pkg._id || idx}
                    className="admin-package-card"
                    style={{
                      background: '#fff',
                      borderRadius: '14px',
                      border: '1.5px solid #e2e8f0',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'stretch',
                      transition: 'box-shadow 0.2s',
                    }}
                  >
                    {/* Image */}
                    <div
                      className="admin-package-img-wrap"
                      style={{
                        width: '140px',
                        flexShrink: 0,
                        position: 'relative',
                        overflow: 'hidden',
                      }}
                    >
                      <img
                        src={pkg.image}
                        alt={pkg.name}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          minHeight: '120px',
                        }}
                        onError={(e) => {
                          e.target.src =
                            'https://images.unsplash.com/photo-1582510003544-4d00b7f74220?auto=format&fit=crop&q=80&w=200'
                        }}
                      />
                      <div
                        style={{
                          position: 'absolute',
                          top: '8px',
                          left: '8px',
                          background: pkg.cat === 'India' ? '#0202bf' : '#7B1FA2',
                          color: '#fff',
                          fontSize: '9px',
                          fontWeight: 800,
                          padding: '3px 8px',
                          borderRadius: '12px',
                          letterSpacing: '0.5px',
                        }}
                      >
                        {pkg.cat || 'India'}
                      </div>
                    </div>

                    {/* Content */}
                    <div
                      className="admin-package-content"
                      style={{
                        flex: 1,
                        padding: '18px 22px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '20px',
                        flexWrap: 'wrap',
                      }}
                    >
                      <div style={{ flex: 1, minWidth: '200px' }}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginBottom: '4px',
                            flexWrap: 'wrap',
                          }}
                        >
                          <span
                            style={{
                              fontSize: '9px',
                              fontWeight: 800,
                              background: '#f8fafc',
                              border: '1px solid #e2e8f0',
                              color: '#6b7a8f',
                              padding: '2px 8px',
                              borderRadius: '10px',
                              letterSpacing: '0.5px',
                            }}
                          >
                            {pkg.code}
                          </span>
                          {pkg.tag && (
                            <span
                              style={{
                                fontSize: '9px',
                                fontWeight: 800,
                                background: 'rgba(255,182,0,0.1)',
                                color: '#b45309',
                                padding: '2px 8px',
                                borderRadius: '10px',
                              }}
                            >
                              {pkg.tag}
                            </span>
                          )}
                        </div>
                        <div
                          style={{
                            fontSize: '15px',
                            fontWeight: 800,
                            color: '#0d1117',
                            marginBottom: '4px',
                            lineHeight: 1.3,
                          }}
                        >
                          {pkg.name}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7a8f' }}>
                          {pkg.nights} Nights · {pkg.brand} · Min. {pkg.minBooking}
                        </div>
                      </div>

                      {/* Price */}
                      <div style={{ textAlign: 'center', minWidth: '100px' }}>
                        <div style={{ fontSize: '11px', color: '#6b7a8f', marginBottom: '2px' }}>
                          From
                        </div>
                        <div style={{ fontSize: '18px', fontWeight: 900, color: '#0202bf' }}>
                          {pkg.from || `₹${pkg.price?.toLocaleString('en-IN')}`}
                        </div>
                        <div style={{ fontSize: '10px', color: '#6b7a8f' }}>per person</div>
                      </div>

                      {/* Stats */}
                      <div style={{ textAlign: 'center', minWidth: '80px' }}>
                        <div style={{ fontSize: '18px', fontWeight: 800, color: '#0d1117' }}>
                          ⭐ {pkg.rating}
                        </div>
                        <div style={{ fontSize: '10px', color: '#6b7a8f' }}>
                          {pkg.reviews} reviews
                        </div>
                      </div>

                      {/* Actions */}
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <button
                          onClick={() => navigate(`/package/${encodeURIComponent(pkg.name)}`)}
                          style={{
                            padding: '8px 16px',
                            background: '#f8fafc',
                            border: '1.5px solid #e2e8f0',
                            borderRadius: '8px',
                            fontSize: '11px',
                            fontWeight: 700,
                            color: '#3d4a5c',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                          }}
                        >
                          <Icon name="eye" size={12} color="#3d4a5c" /> View
                        </button>
                        <button
                          onClick={() => {
                            setEditingPkg(pkg)
                            setView('edit')
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: '6px 12px',
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: 700,
                            color: '#3b82f6',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                          }}
                        >
                          <Icon name="search" size={12} color="#3b82f6" />
                          Edit
                        </button>
                        <button
                          onClick={() => promptDelete(pkg)}
                          disabled={deleting === pkg._id}
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: '6px 12px',
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: 700,
                            color: '#ef4444',
                            cursor: deleting === pkg._id ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                            opacity: deleting === pkg._id ? 0.7 : 1,
                          }}
                        >
                          <Icon name="trash" size={12} color="#ef4444" />
                          {deleting === pkg._id ? 'Deleting...' : 'Delete'}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Footer info */}
            {!loading && packages.length > 0 && (
              <div
                style={{
                  marginTop: '24px',
                  padding: '14px 20px',
                  background: '#fff',
                  borderRadius: '10px',
                  border: '1px solid #e2e8f0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '10px',
                }}
              >
                <span style={{ fontSize: '12px', color: '#6b7a8f' }}>
                  Showing {packages.length} package{packages.length !== 1 ? 's' : ''} · Live data
                </span>
                <button
                  onClick={() => setView('add')}
                  style={{
                    padding: '8px 20px',
                    background: '#0202bf',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '12px',
                    fontWeight: 800,
                    cursor: 'pointer',
                  }}
                >
                  + Add New Package
                </button>
              </div>
            )}
          </>
        )}
      </div>
      {/* Minimal Footer */}
      <footer style={{ marginTop: 'auto', paddingTop: '40px', textAlign: 'center', fontSize: '12px', color: '#6b7a8f' }}>
        © {new Date().getFullYear()} Dira Holidays. All rights reserved. | Admin Package Management Portal
      </footer>
    </div>
  )
}
